import React, { useRef } from 'react';
import Draggable from 'react-draggable';
import { GripHorizontal } from 'lucide-react';

const COLORS = {
  yellow: { bg: '#FEF9C3', border: '#FDE047', header: '#FEF08A', text: '#713F12' },
  pink: { bg: '#FDF2F8', border: '#F9A8D4', header: '#FCE7F3', text: '#831843' },
  blue: { bg: '#F0F9FF', border: '#7DD3FC', header: '#E0F2FE', text: '#0C4A6E' },
  mint: { bg: '#F0FDF4', border: '#86EFAC', header: '#DCFCE7', text: '#14532D' },
  apricot: { bg: '#FFF7ED', border: '#FDBA74', header: '#FFEDD5', text: '#7C2D12' },
  purple: { bg: '#FAF5FF', border: '#D8B4FE', header: '#F3E8FF', text: '#581C87' },
};

export default function StickyModule({ id, title, subtitle, color, rotate, width, position, onStop, onColorChange, draggable, children, badge }) {
  const nodeRef = useRef(null);
  const c = COLORS[color] || COLORS.yellow;

  const note = (
    <div
      ref={nodeRef}
      data-testid={`sticky-note-${id}`}
      className={draggable ? 'absolute' : 'relative w-full'}
      style={{ width: draggable ? width : '100%' }}
    >
      <div
        className="note-enter sticky-shadow rounded-sm"
        style={{
          background: c.bg,
          border: `1.5px solid ${c.border}`,
          transform: `rotate(${rotate}deg)`,
        }}
      >
      <div className="tape" />
      <div
        className={`flex items-center justify-between px-4 pt-4 pb-2.5 select-none ${draggable ? 'drag-handle' : ''}`}
        style={{ background: c.header, borderBottom: `1.5px dashed ${c.border}` }}
        data-testid={`sticky-note-${id}-handle`}
      >
        <div>
          <h2 className="font-display font-bold text-base leading-tight" style={{ color: c.text }}>{title}</h2>
          {subtitle && <p className="font-hand text-sm leading-tight opacity-80" style={{ color: c.text }}>{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {badge}
          {onColorChange && (
            <div className="flex items-center gap-1" data-testid={`color-picker-${id}`}>
              {Object.entries(COLORS).map(([key, val]) => (
                <button
                  key={key}
                  data-testid={`color-dot-${id}-${key}`}
                  onClick={(e) => { e.stopPropagation(); onColorChange(id, key); }}
                  title={key}
                  className={`w-3 h-3 rounded-full border transition-transform hover:scale-125 ${color === key ? 'ring-2 ring-offset-1 ring-slate-400 scale-110' : ''}`}
                  style={{ background: val.header, borderColor: val.border }}
                />
              ))}
            </div>
          )}
          {draggable && <GripHorizontal size={16} style={{ color: c.text, opacity: 0.5 }} />}
        </div>
      </div>
      <div className="p-3.5">{children}</div>
      </div>
    </div>
  );

  if (!draggable) return note;

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".drag-handle"
      position={position}
      onStop={(e, data) => onStop(id, { x: data.x, y: data.y })}
      bounds="parent"
    >
      {note}
    </Draggable>
  );
}
